import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Divider,
  Skeleton,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import { Loading, PanelSkeleton } from '../../components/layout/Skeletons';
import ClaimForm from '../../components/pro/ClaimForm';
import ClaimSearch from '../../components/pro/ClaimSearch';
import ClaimStatus from '../../components/pro/ClaimStatus';
import { useMyListing } from '../../hooks/pro/useMyListing';
import { useMyClaim } from '../../hooks/pro/useMyClaim';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

/**
 * The three questions, in the order somebody answers them: what you do, who
 * you are, how you are reached.
 *
 * Activity comes first because the API cannot create a listing without it: a
 * provider row needs a category (a non-null foreign key) and a name (the slug
 * is derived from it). Asking for the SIRET first meant the wizard's very
 * first save was rejected for two fields the resident had not been shown yet.
 */
const STEPS = [
  { key: 'activity', label: 'Votre activité' },
  { key: 'legal', label: 'Votre statut' },
  { key: 'contact', label: 'Vous joindre' },
];

/** Which fields each step owns, so "next" can refuse to skip a required one. */
const REQUIRED = {
  activity: ['categoryUuid', 'name'],
  // A private individual has no legal identity to give: a student paid
  // through CESU has no SIRET, and asking for one would stop them here.
  legal: ['legalName', 'siret'],
  contact: [],
};

/**
 * First-run wizard for anybody who wants to offer a service.
 *
 * It exists because the listing form asks for eleven things at once, which is
 * fine for editing and daunting on day one. Nothing here is a new capability:
 * it writes the same listing, through the same endpoint, in three passes.
 *
 * Open to residents, not only to accounts that already carry the professional
 * role: writing the listing is what grants it. This used to turn a resident
 * away with "cet espace est réservé aux professionnels" and send them to find
 * a menu entry that granted the role and returned them to the same page —
 * three steps to reach a form they had already asked for.
 *
 * Deliberately not a gate either. A professional is a resident too, and
 * blocking the neighbourhood feed until they have typed a SIRET would be
 * hostile — their listing is invisible until a moderator publishes it anyway.
 * Leaving is always allowed; the banner in the header brings them back.
 */
function ProviderOnboarding() {
  const navigate = useNavigate();
  const toast = useToast();
  // The account is re-read after the first save, which is what grants the
  // professional role: the header and its menu follow without a sign-out.
  const { loading: authLoading, refreshUser } = useAuth();
  const {
    listing,
    formData,
    categories,
    cities,
    choosesCity,
    neighborhoods,
    loading,
    saving,
    error,
    fieldErrors,
    handleInputChange,
    save,
    submit,
    canSubmit,
  } = useMyListing();

  const [step, setStep] = useState(0);
  const [stepError, setStepError] = useState('');

  // Before writing a listing, the account is asked whether its business is
  // already in the directory: an admin may have listed it, and a second one
  // beside it is the duplicate the claim exists to avoid.
  const myClaim = useMyClaim();
  const [creating, setCreating] = useState(false);
  const [claiming, setClaiming] = useState(null);

  if (loading || authLoading || myClaim.loading) {
    return (
      <PageLayout centered={false}>
        <Loading label="Chargement">
          <Box sx={{ width: '100%' }}>
            <Skeleton variant="text" width="50%" height={48} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="70%" height={24} sx={{ mb: 4 }} />
            <PanelSkeleton height={360} />
          </Box>
        </Loading>
      </PageLayout>
    );
  }

  if (null === listing && !creating) {
    const sendClaim = async (values) => {
      if (await myClaim.send(claiming, values)) {
        setClaiming(null);
        toast.success('Votre demande est envoyée. Un modérateur va la vérifier.');
      }
    };

    let content;

    if (myClaim.pending) {
      // Waiting on a moderator. Writing a listing meanwhile is refused by
      // the API, so the only way on is to take the request back.
      content = (
        <>
          <ClaimStatus claim={myClaim.claim} onWithdraw={myClaim.withdraw} saving={myClaim.saving} />
          {myClaim.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {myClaim.error}
            </Alert>
          )}
        </>
      );
    } else if (claiming) {
      content = (
        <>
          <Typography variant="h3" component="h2" sx={{ mb: 2 }}>
            Demander la fiche « {claiming.name} »
          </Typography>
          <ClaimForm
            provider={claiming}
            onSubmit={sendClaim}
            onCancel={() => {
              myClaim.reset();
              setClaiming(null);
            }}
            saving={myClaim.saving}
            error={myClaim.error}
            fieldErrors={myClaim.fieldErrors}
          />
        </>
      );
    } else {
      content = (
        <>
          {/* A refusal is worth reading before asking again. */}
          <ClaimStatus claim={myClaim.claim} sx={{ mb: 3 }} />

          <Typography variant="h3" component="h2" sx={{ mb: 1 }}>
            Votre activité est-elle déjà sur le site ?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            L’équipe du site a pu créer sa fiche avant votre arrivée. Si vous la trouvez,
            demandez-la plutôt que d’en écrire une seconde : ses avis restent, et vous la tenez
            à jour.
          </Typography>

          <ClaimSearch onClaim={setClaiming} />

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button component={RouterLink} to="/" size="large">
              Plus tard
            </Button>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => setCreating(true)}
            >
              Non, je crée ma fiche
            </Button>
          </Box>
        </>
      );
    }

    return (
      <PageLayout centered={false}>
        <Box sx={{ width: '100%' }}>
          <PageHeader
            title="Bienvenue parmi les professionnels"
            lede="Avant tout : votre activité a peut-être déjà sa fiche dans l’annuaire."
          />
          <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 } }}>
            {content}
          </Paper>
        </Box>
      </PageLayout>
    );
  }

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const missingOnThisStep = () =>
    ('legal' === current.key && 'INDIVIDUAL' === formData.legalStatus
      ? []
      : REQUIRED[current.key]
    ).filter((field) => '' === String(formData[field] ?? '').trim());

  // Once "continuer" has been refused, the fields still empty turn red; each
  // one clears as soon as it is filled in.
  const isMissing = (field) => '' !== stepError && missingOnThisStep().includes(field);

  const goNext = async () => {
    const missing = missingOnThisStep();

    if (missing.length > 0) {
      setStepError('Complétez les champs de cette étape pour continuer.');
      return;
    }

    setStepError('');

    // Saved at each step rather than only at the end: somebody interrupted
    // half-way keeps what they typed.
    const wasFirstSave = null === listing;
    const ok = await save();

    if (false === ok) {
      return;
    }

    // That save is what made them a professional; re-read the account so the
    // menu and the banner stop treating them as a resident with no listing.
    if (wasFirstSave) {
      await refreshUser();
    }

    if (isLast) {
      return;
    }

    setStep(step + 1);
  };

  const finish = async () => {
    const wasFirstSave = null === listing;
    const ok = await save();

    if (false === ok) {
      return;
    }

    // Submitting needs the professional role, which that save just granted.
    if (wasFirstSave) {
      await refreshUser();
    }

    const submitted = await submit();

    if (false === submitted) {
      return;
    }

    toast.success('Votre fiche est partie en validation.');
    navigate('/pro/ma-fiche');
  };

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          title="Bienvenue parmi les professionnels"
          lede="Trois étapes, puis un modérateur relit votre fiche avant qu'elle soit visible. Vous pouvez partir et revenir : ce que vous saisissez est enregistré à chaque étape."
        />

        <Stepper activeStep={step} sx={{ mb: 4 }}>
          {STEPS.map((s) => (
            <Step key={s.key}>
              <StepLabel>{s.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 } }}>
          {'legal' === current.key && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                select
                label="Vous exercez comme"
                name="legalStatus"
                value={formData.legalStatus}
                onChange={handleInputChange}
                helperText={
                  'INDIVIDUAL' === formData.legalStatus
                    ? 'Le paiement se fait de particulier à particulier (CESU, chèque emploi service).'
                    : 'Entreprise, auto-entrepreneur, association.'
                }
                disabled={saving || 'COMMERCE' === formData.kind}
              >
                <MenuItem value="COMPANY">Une entreprise</MenuItem>
                <MenuItem value="INDIVIDUAL">Un particulier</MenuItem>
              </TextField>

              {'INDIVIDUAL' === formData.legalStatus && (
                <Typography variant="body2" color="text.secondary">
                  Rien d’autre à remplir ici. Votre fiche indiquera « Particulier » aux
                  habitants.
                </Typography>
              )}

              {'COMPANY' === formData.legalStatus && (
                <>
              <Typography variant="body2" color="text.secondary">
                Ces informations ne sont pas publiées publiquement : elles sont réservées à
                l’équipe de modération.
              </Typography>

              <TextField
                label="Raison sociale"
                name="legalName"
                value={formData.legalName}
                onChange={handleInputChange}
                error={Boolean(fieldErrors.legalName) || isMissing('legalName')}
                helperText={fieldErrors.legalName || (isMissing('legalName') ? 'Ce champ est obligatoire' : 'Le nom de l’entreprise sur ses documents')}
                required
                disabled={saving}
                inputProps={{ maxLength: 180 }}
              />

              <TextField
                label="SIRET"
                name="siret"
                value={formData.siret}
                onChange={handleInputChange}
                error={Boolean(fieldErrors.siret) || isMissing('siret')}
                helperText={fieldErrors.siret || (isMissing('siret') ? 'Ce champ est obligatoire' : '14 chiffres, espaces autorisés')}
                required
                disabled={saving}
                inputProps={{ maxLength: 20 }}
              />
                </>
              )}
            </Box>
          )}

          {'activity' === current.key && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Asked before the trade, because it decides which list of
                  trades comes next: a shopfront and somebody who comes to you
                  are not filed under the same headings. */}
              <TextField
                select
                label="Vous êtes"
                name="kind"
                value={formData.kind}
                onChange={handleInputChange}
                helperText={
                  'COMMERCE' === formData.kind
                    ? 'Les habitants verront vos horaires et vos offres du moment.'
                    : 'Les habitants pourront vous demander un rendez-vous.'
                }
                disabled={saving}
              >
                <MenuItem value="SERVICE">Un prestataire de services</MenuItem>
                <MenuItem value="COMMERCE">Un commerce</MenuItem>
              </TextField>

              <TextField
                select
                label={'COMMERCE' === formData.kind ? 'Type de commerce' : 'Métier'}
                name="categoryUuid"
                value={formData.categoryUuid}
                onChange={handleInputChange}
                error={Boolean(fieldErrors.categoryUuid) || isMissing('categoryUuid')}
                helperText={fieldErrors.categoryUuid || (isMissing('categoryUuid') ? 'Ce champ est obligatoire' : 'Là où les habitants vous chercheront')}
                required
                disabled={saving}
              >
                {categories.map((category) => (
                  <MenuItem
                    key={category.uuid}
                    value={category.uuid}
                    sx={{ pl: category.depth > 0 ? 4 : 2 }}
                  >
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Nom commercial"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={Boolean(fieldErrors.name) || isMissing('name')}
                helperText={fieldErrors.name || (isMissing('name') ? 'Ce champ est obligatoire' : 'Le nom sous lequel les habitants vous connaissent')}
                required
                disabled={saving}
              />

              <TextField
                label="Ce que vous faites"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                minRows={4}
                disabled={saving}
                placeholder="Vos prestations, votre zone d’intervention, vos délais habituels."
              />

              {/* Where the business is, which is not always where its owner
                  lives. Asked only when more than one city is open. */}
              {choosesCity && (
                <TextField
                  select
                  label="Ville de l’activité"
                  name="citySlug"
                  value={formData.citySlug}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!fieldErrors.citySlug}
                  helperText={fieldErrors.citySlug || 'Là où vous exercez, pas forcément là où vous habitez.'}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.slug} value={city.slug}>
                      {city.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {neighborhoods.length > 0 && (
                <TextField
                  select
                  label="Quartier (facultatif)"
                  name="neighborhoodUuid"
                  value={formData.neighborhoodUuid}
                  onChange={handleInputChange}
                  disabled={saving}
                >
                  <MenuItem value="">Toute la ville</MenuItem>
                  {neighborhoods.map((neighborhood) => (
                    <MenuItem key={neighborhood.uuid} value={neighborhood.uuid}>
                      {neighborhood.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          )}

          {'contact' === current.key && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Facultatif : téléphone, email et site web peuvent être renseignés plus tard.
              </Typography>

              <TextField
                label="Téléphone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                error={Boolean(fieldErrors.phone)}
                helperText={fieldErrors.phone || ' '}
                disabled={saving}
              />

              <TextField
                label="Email de contact"
                name="contactEmail"
                type="email"
                value={formData.contactEmail}
                onChange={handleInputChange}
                error={Boolean(fieldErrors.contactEmail)}
                helperText={fieldErrors.contactEmail || ' '}
                disabled={saving}
              />

              <TextField
                label="Site web"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                error={Boolean(fieldErrors.website)}
                helperText={fieldErrors.website || ' '}
                disabled={saving}
              />

              <TextField
                label="Adresse (facultatif)"
                name="street"
                value={formData.street}
                onChange={handleInputChange}
                disabled={saving}
              />
            </Box>
          )}

          {(stepError || error) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {stepError || error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mt: 4,
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'surface.rule',
              flexWrap: 'wrap',
            }}
          >
            <Button
              size="large"
              startIcon={<ArrowBackIcon />}
              disabled={0 === step || saving}
              onClick={() => {
                setStepError('');
                setStep(step - 1);
              }}
            >
              Précédent
            </Button>

            <Box sx={{ flexGrow: 1 }} />

            {/* Always available: a professional is a resident too, and this
                must never be a cage. */}
            <Button component={RouterLink} to="/" disabled={saving} size="large">
              Plus tard
            </Button>

            {isLast ? (
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckIcon />}
                disabled={saving || !canSubmit}
                onClick={finish}
              >
                Envoyer en validation
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                disabled={saving}
                onClick={goNext}
              >
                Continuer
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </PageLayout>
  );
}

export default ProviderOnboarding;
